import { gql } from '@plato/app-core/gql';
import type { CustomerSort, CustomerStatus, InsightType } from '@plato/app-core/gql/schema-types';
import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useQuery } from '@plato/app-core/hooks/use-query';
import { useInsightTypeLabel } from '@plato/app-shared/hooks/use-insight-type-label';
import type { CustomerListParams } from '@plato/app-shared/utils/customer-list';
import { omitEmpty } from '@plato/shared-util/object';
import { mergeFilterOptions } from '@plato/ui-legacy/components/filters/lib/merge-filter-options';
import type {
    CheckboxFilterOption,
    VerticalFilterOption,
} from '@plato/ui-legacy/components/filters/types/filter-types';
import { VerticalFilters } from '@plato/ui-legacy/components/filters/vertical-filters';
import { omit } from 'lodash-es';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useCustomerListParams } from '../hooks/use-customer-list-params.ts';

const CUSTOMER_LIST_FILTER_OPTIONS_QUERY = gql(/* GraphQL */ `
    query CustomerListFilterOptions(
        $filter: CustomerFilter
        $customerOptionsFilter: CustomerFilter
        $appliedCustomerIds: [ID!]!
        $hasAppliedCustomers: Boolean!
        $appliedSalesRepIds: [ID!]!
        $hasAppliedSalesReps: Boolean!
        $appliedCompanyIds: [ID!]!
        $hasAppliedCompanies: Boolean!
        $sortBy: CustomerSort!
        $first: Int
    ) {
        customers(filter: $filter, sortBy: $sortBy) {
            totalCount
            filterOptions {
                cityNames {
                    value
                    count
                }
                zipCodes {
                    value
                    count
                }
                industryNames {
                    value
                    count
                }
                customerClasses {
                    value
                    count
                }
                salesRegions {
                    value
                    count
                }
                statuses {
                    value
                    count
                }
                insightTypes {
                    value
                    count
                }
                salesRepIds {
                    salesRep {
                        id
                        name
                    }
                    count
                }
                companyIds {
                    company {
                        id
                        name
                    }
                    count
                }
            }
        }
        customerOptions: customers(filter: $customerOptionsFilter, sortBy: $sortBy, first: $first) {
            nodes {
                id
                name
            }
        }
        appliedCustomers: customers(filter: { ids: $appliedCustomerIds }, sortBy: $sortBy, first: $first)
            @include(if: $hasAppliedCustomers) {
            nodes {
                id
                name
            }
        }
        appliedSalesReps: salesReps(ids: $appliedSalesRepIds) @include(if: $hasAppliedSalesReps) {
            id
            name
        }
        appliedCompanies: companies(ids: $appliedCompanyIds) @include(if: $hasAppliedCompanies) {
            id
            name
        }
    }
`);

const statusLabelKeys: Record<CustomerStatus, string> = {
    new: 'status_new',
    active: 'status_active',
    at_risk: 'status_at_risk',
    dormant: 'status_dormant',
    inactive: 'status_inactive',
    blocked: 'status_blocked',
};

const filterOptionsSortBy: CustomerSort = { by: { field: 'customer_name' }, direction: 'asc' };

// TODO(AEP-213): the customer picker loads its options client-side, so cap the
// list until it moves to server-side search (CheckboxFilter.onSearchOptions).
const customerFilterOptionsLimit = 500;

// Customer lifecycle order, not alphabet: a rep reads this list as a funnel from won to lost.
const statusSortOrder: CustomerStatus[] = ['new', 'active', 'at_risk', 'dormant', 'inactive', 'blocked'];

// Ordered by how actionable the recommendation is, most urgent first.
const insightTypeSortOrder: InsightType[] = [
    'ChurnWarningWebCategory',
    'XSellWebCategory',
    'ShareOfWalletCategory',
    'IndustryTopCategory',
    'PreviouslyPurchasedCategory',
    'FirstPurchaseWebCat3',
    'GlobalTopCategory',
];

function mergeSelfLabelledOptions(
    options: { value: string; count: number }[] | undefined,
    appliedValues: string[] | null | undefined,
): CheckboxFilterOption['options'] {
    return mergeFilterOptions(
        options,
        appliedValues?.map((value) => ({ value, count: 0 })),
        (option) => ({
            id: option.value,
            label: option.value,
            value: option.value,
            count: option.count,
        }),
        appliedValues,
    );
}

export function CustomerListFilters(): ReactElement {
    const { t } = useTranslation('customer-list');
    const insightTypeLabel = useInsightTypeLabel();
    const {
        defaultCurrency,
        enablePlatoStatus,
        canFilterCustomerBySalesRep,
        canFilterCustomerBySalesLocation,
        canFilterCustomerByCustomerClass,
        canFilterCustomerBySalesRegion,
    } = useAppConfig();

    const { params, setParams } = useCustomerListParams();

    //-----------------------------------------------------------------------------------------------
    // Modal editing state
    //-----------------------------------------------------------------------------------------------

    const [isOpen, setIsOpen] = useState(false);
    const [modalEditFilters, setModalEditFilters] = useState<CustomerListParams>(params);

    const modalEditFilter = omitEmpty(omit(modalEditFilters, 'sorting'));

    const [{ data: filterOptionsData, fetching: isFetchingFilterOptions }] = useQuery({
        query: CUSTOMER_LIST_FILTER_OPTIONS_QUERY,
        variables: {
            filter: modalEditFilter,
            customerOptionsFilter: { ...modalEditFilter, ids: undefined },
            appliedCustomerIds: params.ids ?? [],
            hasAppliedCustomers: !!params.ids?.length,
            appliedSalesRepIds: params.salesRepIds ?? [],
            hasAppliedSalesReps: !!params.salesRepIds?.length,
            appliedCompanyIds: params.companyIds ?? [],
            hasAppliedCompanies: !!params.companyIds?.length,
            sortBy: filterOptionsSortBy,
            first: customerFilterOptionsLimit,
        },
        pause: !isOpen,
    });
    const filterOptions = filterOptionsData?.customers.filterOptions;
    const editingModalCount = filterOptionsData?.customers.totalCount;
    const isLoadingCount = editingModalCount === undefined && (isFetchingFilterOptions || isOpen);

    const customerOptions = filterOptionsData?.customerOptions.nodes ?? [];
    const appliedCustomers = filterOptionsData?.appliedCustomers?.nodes ?? [];
    const appliedSalesReps = (filterOptionsData?.appliedSalesReps ?? []).filter(
        (salesRep) => salesRep !== null,
    );
    const appliedCompanies = (filterOptionsData?.appliedCompanies ?? []).filter(
        (company) => company !== null,
    );

    // Ordered by how many distinct users actually apply each filter (PostHog, 180d), with the
    // geography filters (city / zip / region) and the customer attributes kept adjacent.
    const filters: VerticalFilterOption<CustomerListParams>[] = [
        {
            id: 'insightTypes' as const,
            label: t('filter_menu_title__insights'),
            type: 'checkbox',
            options: mergeFilterOptions(
                // Only surface insight types that have a UI label mapping; unmapped
                // types (e.g. ChurnWarningV2WebCategory) are hidden rather than shown raw.
                filterOptions?.insightTypes.filter((o) => insightTypeLabel(o.value)),
                params.insightTypes
                    ?.filter((value) => insightTypeLabel(value))
                    .map((value) => ({ value, count: 0 })),
                (o) => {
                    return {
                        id: o.value,
                        label: insightTypeLabel(o.value) ?? o.value,
                        value: o.value,
                        count: o.count,
                    };
                },
                params.insightTypes,
                { sortOrder: insightTypeSortOrder },
            ),
        },
        {
            id: 'salesRepIds' as const,
            label: t('filter_menu_title__sales_rep'),
            type: 'checkbox',
            options: mergeFilterOptions(
                filterOptions?.salesRepIds.map((o) => ({
                    id: o.salesRep.id,
                    label: o.salesRep.name ? `${o.salesRep.id}: ${o.salesRep.name}` : o.salesRep.id,
                    value: o.salesRep.id,
                    count: o.count,
                })),
                appliedSalesReps.map((salesRep) => ({
                    id: salesRep.id,
                    label: salesRep.name ? `${salesRep.id}: ${salesRep.name}` : salesRep.id,
                    value: salesRep.id,
                    count: 0,
                })),
                (option) => option,
                params.salesRepIds,
            ),
        },
        {
            id: 'statuses' as const,
            label: t('filter_menu_title__status'),
            type: 'checkbox',
            options: mergeFilterOptions(
                filterOptions?.statuses,
                params.statuses?.map((value) => ({ value, count: 0 })),
                (o) => ({
                    id: o.value,
                    label: t(statusLabelKeys[o.value]),
                    value: o.value,
                    count: o.count,
                }),
                params.statuses,
                { sortOrder: statusSortOrder },
            ),
        },
        {
            id: 'cityNames' as const,
            label: t('filter_menu_title__city'),
            type: 'checkbox',
            options: mergeSelfLabelledOptions(filterOptions?.cityNames, params.cityNames),
        },
        {
            id: 'zipCodes' as const,
            label: t('filter_menu_title__zip_code'),
            type: 'checkbox',
            options: mergeSelfLabelledOptions(filterOptions?.zipCodes, params.zipCodes),
        },
        {
            id: 'salesRegions' as const,
            label: t('filter_menu_title__sales_region'),
            type: 'checkbox',
            options: mergeSelfLabelledOptions(filterOptions?.salesRegions, params.salesRegions),
        },
        {
            id: 'ids' as const,
            label: t('filter_menu_title__customer'),
            type: 'checkbox',
            options: mergeFilterOptions(
                customerOptions.map((customer) => ({
                    id: customer.id,
                    label: customer.name ?? customer.id,
                    value: customer.id,
                })),
                appliedCustomers.map((customer) => ({
                    id: customer.id,
                    label: customer.name ?? customer.id,
                    value: customer.id,
                })),
                (option) => option,
                params.ids,
            ),
        },
        {
            id: 'customerClasses' as const,
            label: t('filter_menu_title__customer_class'),
            type: 'checkbox',
            options: mergeSelfLabelledOptions(filterOptions?.customerClasses, params.customerClasses),
        },
        {
            id: 'industryNames' as const,
            label: t('filter_menu_title__industry'),
            type: 'checkbox',
            options: mergeSelfLabelledOptions(filterOptions?.industryNames, params.industryNames),
        },
        {
            id: 'companyIds' as const,
            label: t('filter_menu_title__company'),
            type: 'checkbox',
            options: mergeFilterOptions(
                filterOptions?.companyIds.map((o) => ({
                    id: o.company.id ?? '',
                    label: o.company.name ?? o.company.id ?? '',
                    value: o.company.id ?? '',
                    count: o.count,
                })),
                appliedCompanies.map((company) => ({
                    id: company.id ?? '',
                    label: company.name ?? company.id ?? '',
                    value: company.id ?? '',
                    count: 0,
                })),
                (option) => option,
                params.companyIds,
            ),
        },
    ];

    const allowedFilters = filters.filter(({ id }) => {
        switch (id) {
            case 'salesRepIds':
                return canFilterCustomerBySalesRep;
            case 'statuses':
                return enablePlatoStatus;
            case 'companyIds':
                return canFilterCustomerBySalesLocation;
            case 'customerClasses':
                return canFilterCustomerByCustomerClass;
            case 'salesRegions':
                return canFilterCustomerBySalesRegion;
            default:
                return true;
        }
    });

    return (
        <VerticalFilters<CustomerListParams>
            open={isOpen}
            setOpen={setIsOpen}
            appliedFilters={params}
            onApplyFilters={setParams}
            onFiltersChange={setModalEditFilters}
            searchFilterKey="searchString"
            searchPlaceholder={t('customer_search_placeholder')}
            itemCount={editingModalCount ?? 0}
            isLoading={isLoadingCount}
            defaultCurrency={defaultCurrency}
            filters={allowedFilters}
            defaultFilters={{ sorting: params.sorting }}
        />
    );
}
